<template>
	<div>
		<!-- error message -->
		<div class="alert alert-danger" v-if="errorMessage">
			<strong class="text-uppercase">{{ errorMessage }}</strong>
		</div>

		<!-- success message -->
		<div class="alert alert-success" v-if="successMessage">
			<strong class="text-uppercase">{{ successMessage }}</strong>
		</div>

		<form @submit.prevent="preSave">
			<!-- dados pessoais -->
			<div class="panel panel-custom">
				<div class="panel-heading">
					<i class="fa fa-user pull-right" style="margin-top:5px;"></i>
					<strong class="text-uppercase">Dados Pessoais</strong>
				</div>
				<div class="panel-body" style="padding-bottom:5px;">
					<v-input
						label="NOME"
						v-model="form.nome"
						maxlength="200"
						@keypress="preventNumbers"
						:error-message="validate('isNotBlank', form.nome, 'Preenchimento obrigatório')"
					></v-input>

					<v-input
						type="tel"
						label="DATA DE NASCIMENTO"
						v-model="form.dataNascimento"
						placeholder="DD/MM/AAAA"
						:mask="{ format: '99/99/9999', change: true }"
						:error-message="form.dataNascimento !== '' ? validate('isValidDate', form.dataNascimento, 'Data inválida') : ''"
					></v-input>

					<v-input
						type="tel"
						label="CPF"
						v-model="form.cpf"
						:mask="{ format: '99999999999' }"
						:error-message="validate('isValidCPF', form.cpf, 'CPF inválido') || invalidCPF"
						@input="checkCPF(form.cpf)"
					></v-input>

					<v-input
						label="RG"
						v-model="form.rg"
						maxlength="15"
						:error-message="validate('isNotBlank', form.rg, 'Preenchimento obrigatório')"
					></v-input>
				</div>
			</div>

			<!-- endereço -->
			<div class="panel panel-custom">
				<div class="panel-heading">
					<i class="fa fa-map pull-right" style="margin-top:5px;"></i>
					<strong class="text-uppercase">Endereço</strong>
				</div>
				<div class="panel-body" style="padding-bottom:5px;">
					<div class="form-group">
						<label class="control-label">ENDEREÇO COM CEP?</label>
						<p class="form-control-static">
							<v-radio
								v-model="form.enderecoNormalizado"
								option-text="SIM"
								:option-value="true"
							></v-radio>

							<v-radio
								v-model="form.enderecoNormalizado"
								option-text="NÃO"
								:option-value="false"
							></v-radio>
						</p>
					</div>

					<div class="form-group" :class="{ 'has-error': invalidCep || (validating && form.enderecoCep.idCep.length < 8) }" v-if="form.enderecoNormalizado">
						<label for="cep" class="control-label">CEP</label>
						<form @submit.prevent="buscarCEP">
							<div class="input-group">
								<v-input
									type="tel"
									id="cep"
									v-model="form.enderecoCep.idCep"
									styles="border-radius:4px 0 0 4px;"
									:mask="{ format: '99999-999' }"
									:wrapped="false"
									@keyup="$event.target.value.length === 9 ? buscarCEP() : ''"
								></v-input>
								<span class="input-group-btn">
									<button type="submit" class="btn btn-warning" :disabled="form.enderecoCep.idCep.length < 8">
										<i class="fa fa-search"></i>
									</button>
								</span>
							</div>
						</form>
						<span class="help-block" v-if="validate('isValidCEP', form.enderecoCep.idCep, true)">CEP inválido</span>
						<span class="help-block" v-if="invalidCep">Somente endereços da cidade de Sorocaba são permitidos</span>
					</div>

					<v-input
						label="LOGRADOURO"
						v-model="form.enderecoLogradouro"
						maxlength="100"
						:disabled="form.enderecoNormalizado"
						:error-message="validate('isNotBlank', form.enderecoLogradouro, 'Preenchimento obrigatório')"
					></v-input>

					<v-input
						label="NÚMERO"
						v-model="form.enderecoNumero"
						maxlength="10"
						:error-message="validate('isNotBlank', form.enderecoNumero, 'Preenchimento obrigatório')"
					></v-input>

					<v-input
						label="COMPLEMENTO"
						v-model="form.enderecoComplemento"
						maxlength="100"
					></v-input>

					<v-input
						label="BAIRRO"
						v-model="form.enderecoBairro"
						maxlength="100"
						:disabled="form.enderecoNormalizado"
						:error-message="validate('isNotBlank', form.enderecoBairro, 'Preenchimento obrigatório')"
					></v-input>

					<div class="row">
						<v-input label="CIDADE" cols="10" v-model="form.enderecoCep.localidade" :disabled="true"></v-input>
						<v-input label="ESTADO" cols="2" v-model="form.enderecoCep.ufSigla" :disabled="true"></v-input>
					</div>
				</div>
			</div>

			<!-- contato -->
			<div class="panel panel-custom">
				<div class="panel-heading">
					<i class="fa fa-phone pull-right" style="margin-top:5px;"></i>
					<strong class="text-uppercase">Contato</strong>
				</div>
				<div class="panel-body" style="padding-bottom:5px;">
					<v-input
						type="tel"
						label="TELEFONE FIXO"
						v-model="form.telefoneFixo"
						maxlength="20"
						:mask="{ format: '(99) 9999-9999', change: true }"
						:error-message="form.telefoneFixo === '' ? validate('isNotBlank', form.telefoneFixo, 'Preenchimento obrigatório') : validate('isValidTelephone', form.telefoneFixo, 'Telefone inválido')"
					></v-input>

					<div class="row">
						<div class="col-sm-6">
							<v-input
								type="tel"
								label="TELEFONE CELULAR"
								v-model="form.telefoneCelular"
								maxlength="20"
								:mask="{ format: '(99) 99999-9999', change: true }"
								:error-message="form.telefoneCelular === '' ? validate('isNotBlank', form.telefoneCelular, 'Preenchimento obrigatório') : validate('isValidCelphone', form.telefoneCelular, 'Telefone inválido')"
							></v-input>
						</div>
						<div class="col-sm-6">
							<div class="form-group" :class="{ 'has-error': validating && form.telefoneCelularWhatsapp === '' }">
								<label class="control-label">TEM WHATSAPP?</label>
								<p class="form-control-static">
									<v-radio
										:option-value="true"
										option-text="SIM"
										v-model="form.telefoneCelularWhatsapp"
									></v-radio>

									<v-radio
										:option-value="false"
										option-text="NÃO"
										v-model="form.telefoneCelularWhatsapp"
									></v-radio>
								</p>
								<span class="help-block" v-if="validating && form.telefoneCelularWhatsapp === ''">Preenchimento obrigatório</span>
							</div>
						</div>
					</div>

					<v-input
						label="E-MAIL"
						v-model="form.email"
						maxlength="100"
						:error-message="form.email !== '' ? validate('isValidEmail', form.email, 'E-mail inválido') : ''"
					></v-input>
				</div>
			</div>

			<!-- segurança -->
			<div class="panel panel-custom">
				<div class="panel-heading">
					<i class="fa fa-lock pull-right" style="margin-top:5px;"></i>
					<strong class="text-uppercase">Segurança</strong>
				</div>
				<div class="panel-body" style="padding-bottom:5px;">
					<template v-if="updating">
						<div class="form-group">
							<label class="control-label">ALTERAR SENHA?</label>
							<div>
								<v-radio
									:option-value="true"
									option-text="SIM"
									v-model="showSenhaFields"
								></v-radio>

								<v-radio
									:option-value="false"
									option-text="NÃO"
									v-model="showSenhaFields"
								></v-radio>
							</div>
						</div>
					</template>

					<template v-if="showSenhaFields">
						<v-input
							type="password"
							label="SENHA ATUAL"
							v-model="form.senhaAtual"
							v-if="updating"
							maxlength="20"
							:error-message="validate('hasMinLength', form.senhaAtual, 5, 'Deve conter ao menos 5 caracteres')"
						></v-input>

						<v-input
							type="password"
							:label="updating ? 'NOVA SENHA' : 'SENHA'"
							v-model="form.senha"
							maxlength="20"
							:error-message="validating
								? form.senha.length < 5
									? 'Deve conter ao menos 5 caracteres'
									: form.senha !== form.novaSenha
										? 'As senhas não coincidem'
										: ''
								: ''
							"
						></v-input>

						<v-input
							type="password"
							:label="updating ? 'REPITA A NOVA SENHA' : 'REPITA A SENHA'"
							v-model="form.novaSenha"
							maxlength="20"
							:error-message="validating
								? form.novaSenha.length < 5
									? 'Deve conter ao menos 5 caracteres'
									: form.senha !== form.novaSenha
										? 'As senhas não coincidem'
										: ''
								: ''
							"
						></v-input>
					</template>
				</div>
			</div>

			<div class="panel panel-custom">
				<div class="panel-body">
					<template v-if="confirming">
						<form @submit.prevent="save" class="form-horizontal">
							<div class="form-group" style="margin-bottom:0;">
								<div class="col-xs-6">
									<label class="control-label text-uppercase">Confirma?</label>
								</div>
								<div class="col-xs-6 text-right">
									<button type="button" class="btn btn-danger" @click="confirming = false">NÃO</button>
									<button type="submit" class="btn btn-success">SIM</button>
								</div>
							</div>
						</form>
					</template>
					<template v-else>
						<button type="submit" class="btn btn-success pull-right">SALVAR</button>
					</template>
				</div>
			</div>
		</form>
	</div>
</template>

<script>
	import $ from 'jquery';
	import Form from 'helpers/form';
	import rules from 'helpers/validationRules';
	import Municipe from 'models/municipe';

	export default {
		props: {
			municipe: { default: '' }
		},

		data() {
			let updating = this.municipe && this.municipe.id ? true : false;
			let showSenhaFields = updating ? false : true;

			return {
				confirming: false,
				validating: false,
				updating: updating,
				invalidCPF: false,
				invalidCep: false,
				showSenhaFields: showSenhaFields,
				errorMessage: '',
				successMessage: '',

				form: new Form({
					nome: '',
					dataNascimento: '',
					cpf: '',
					rg: '',
					enderecoNormalizado: true,
					enderecoLogradouro: '',
					enderecoNumero: '',
					enderecoComplemento: '',
					enderecoBairro: '',
					enderecoCep: {
						idCep: '',
						localidade: 'Sorocaba',
						ufSigla: 'SP'
					},
					telefoneFixo: '',
					telefoneCelular: '',
					telefoneCelularWhatsapp: '',
					email: '',
					senhaAtual: '',
					senha: '',
					novaSenha: ''
				})
			};
		},

		watch: {
			'form.enderecoNormalizado'() {
				// não dispara quando o created é executado
				if (this.preventWatcher === undefined) {
					this.form.enderecoCep.idCep = '';
					this.form.enderecoLogradouro = '';
					this.form.enderecoNumero = '';
					this.form.enderecoComplemento = '';
					this.form.enderecoBairro = '';
				}
			},

			'form.telefoneCelular'() {
				if (this.form.telefoneCelular === '') {
					this.form.telefoneCelularWhatsapp = '';
				}
			}
		},

		created() {
			if (this.municipe) {
				// previne que o watch do enderecoNormalizado seja disparado
				this.preventWatcher = true;

				for (let field in this.municipe) {
					this.form[field] = this.municipe[field];
				}

				if (this.municipe.enderecoCep) {
					this.form.enderecoLogradouro = this.municipe.enderecoCep.logradouro;
					this.form.enderecoBairro = this.municipe.enderecoCep.bairro;
				}

				// habilita watch do enderecoNormalizado novamente
				this.$nextTick(() => {
					this.preventWatcher = undefined;
				});
			}
		},

		methods: {
			validate(rule, value, arg, message) {
				if (message === undefined) {
					message = arg;
				}

				if (this.validating) {
					return rules[rule](value, arg) ? '' : message;
				}
			},

			preventNumbers(e) {
				if (/\d/.test(e.key)) {
					e.preventDefault();
				}
			},

			async checkCPF(value) {
				this.invalidCPF = '';

				if (rules.isValidCPF(value)) {
					let options = {
						url: `${Config.BASE_URL}/municipe/consultaCpf`,
						data: {
							cpf: value
						}
					};

					let CPFExists = await Municipe.checkCPF(options);

					if (CPFExists) {
						this.invalidCPF = 'CPF já está cadastrado';
					}
				}
			},

			hasValidationErrors() {
				let fields = $('.has-error');

				return fields.length > 0;
			},

			async buscarCEP() {
				let options = {
					url: `${Config.BASE_URL}/municipe/consultaCep`,
					data: {
						cep: this.form.enderecoCep.idCep
					}
				};

				let endereco = await Municipe.checkCEP(options);

				if (endereco) {
					this.form.enderecoLogradouro = endereco.logradouro;
					this.form.enderecoBairro = endereco.bairro;
				} else {
					this.form.enderecoLogradouro = '';
					this.form.enderecoBairro = '';
					this.form.enderecoCep.idCep = '';
					this.invalidCep = true;

					setTimeout(() => {
						this.invalidCep = false;
					}, 3000);
				}
			},

			preSave() {
				this.validating = true;
				this.errorMessage = '';

				this.$nextTick(() => {
					if (this.hasValidationErrors() === false) {
						this.confirming = true;
					} else {
						let firstErrorField = $('.has-error:first');

						window.scrollTo(0, firstErrorField.position().top);
					}
				});
			},

			save() {
				let url = `${Config.BASE_URL}/municipe`;

				this.confirming = false;
				this.validating = false;

				this.form
					.save(url)
					.then(() => {
						if (this.updating) {
							this.successMessage = 'Cadastro atualizado com sucesso!';
							this.showSenhaFields = false;

							this.form.senhaAtual = '';
							this.form.senha = '';
							this.form.novaSenha = '';
						} else {
							location.reload();
						}
					})
					.catch(error => {
						this.errorMessage = error.responseJSON.message || error.responseJSON.errorMessage;
					})
					.done(() => {
						window.scrollTo(0, 0);

						setTimeout(() => {
							this.errorMessage = '';
							this.successMessage = '';
						}, 5000);
					});
			}
		}
	};
</script>


// WEBPACK FOOTER //
// form.vue?b15848d8